<script setup>
import { ref } from 'vue';
import { API } from '@/services/index';
import { useQueryClient, useMutation } from '@tanstack/vue-query';
import { useUserStore } from '@/stores/user.store.js';
import { useAccountStore } from '@/stores/account.store.js';
import { useSettingsStore } from '@/stores/settings.store.js';
import { useWorkspaceStore } from '@/stores/workspace.store.js';
import { GRANT_USER_ROLES_TO_ACCOUNT } from '@/services/account/mutations/account';
import partnrUpShortLogo from '@/assets/logo/PartnrUP_P_logo.png';

const formRules = {
  emails: (value) => value.length > 0 || 'Email field is required.',
  role: (value) => !!value || 'Role field is required.'
};

const props = defineProps({
  isDisabled: {
    type: Boolean,
    default: false
  },
  accountRoles: {
    type: Array,
    default: () => [],
    required: true
  }
});

const queryClient = useQueryClient();
const dialog = ref(false);
const activeAccount = computed(() => useSettingsStore().activeAccount);
const workspaces = computed(() => useSettingsStore().workspaces);
const isPermissionsLoading = computed(() => useUserStore().isPermissionsLoading);
const isFormValid = ref(false);
const formData = ref({
  emails: '',
  role: null,
  workspaces: [],
  inviteToTeam: false
});

const selectedAllWorkspaces = computed(() => formData.value.workspaces.length === workspaces.value.length);
const selectedSomeWorkspaces = computed(() => formData.value.workspaces.length > 0);

const { isPending, isError, error, isSuccess, mutate } = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/account', GRANT_USER_ROLES_TO_ACCOUNT, payload);
  },
  onSuccess: async (data) => {
    if (data.data.data.grantUserRole) {
      dialog.value = false;
      clearForm();
      await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
    }

    if (data.data.error) {
      console.log(data.data.error);
    }
  },
  onError: (error) => {
    console.log('error', error);
  }
});

const selectAllWorkspaces = () => {
  if (selectedAllWorkspaces.value) {
    formData.value.workspaces = [];
  } else {
    formData.value.workspaces = workspaces.value;
  }
};

const onSubmitHandler = async () => {
  const payload = {
    account_id: Number(activeAccount.value.id),
    email: formData.value.emails,
    role: formData.value.role.id,
    workspace_ids: formData.value.workspaces.map((workspace) => Number(workspace.id)),
    invite_to_team: formData.value.inviteToTeam
  };
  await mutate(payload);
};

const clearForm = () => {
  formData.value = {
    emails: '',
    role: null,
    workspaces: [],
    inviteToTeam: false
  };
};
</script>

<template>
  <div class="tw-hidden lg:tw-block">
    <v-btn
      :disabled="isPermissionsLoading || isDisabled"
      @click="dialog = true"
      class="tw-text-[0.79rem] tw-capitalize"
      color="primary-blue"
      prepend-icon="mdi-plus"
    >
      Invite Team Members
    </v-btn>
  </div>
  <div class="tw-block lg:tw-hidden">
    <v-btn icon="mdi-account-multiple-plus" :disabled="isPermissionsLoading || isDisabled" @click="dialog = true" color="primary-blue"> </v-btn>
  </div>

  <v-dialog v-model="dialog" @update:modelValue="clearForm" width="auto" attach="#app" :persistent="isPending">
    <v-card class="tw-rounded-xl" max-width="702" width="702" :loading="isPending">
      <div class="tw-px-5 tw-pb-5">
        <h2 class="tw-my-3 tw-text-center tw-text-[0.79rem] tw-text-xl tw-font-bold tw-capitalize tw-text-primary-blue">Invite Team Members</h2>
        <div class="tw-rounded-xl tw-bg-[#f7f7f7] tw-p-10">
          <v-form ref="inviteForm" v-model="isFormValid" @submit.prevent="onSubmitHandler">
            <div class="tw-flex tw-flex-col tw-space-y-5">
              <div class="tw-flex tw-w-full tw-items-start tw-justify-between tw-space-x-3">
                <div class="tw-flex tw-w-full">
                  <v-textarea
                    width="455"
                    color="primary-blue"
                    v-model="formData.emails"
                    placeholder="Add emails"
                    hide-details="auto"
                    variant="solo"
                    clearable
                    rows="1"
                    auto-grow
                    density="compact"
                    :disabled="isPending"
                    :rules="[formRules.emails]"
                  ></v-textarea>
                </div>
                <div class="tw-flex tw-w-full">
                  <v-select
                    min-width="141"
                    width="141"
                    v-model="formData.role"
                    :items="accountRoles"
                    return-object
                    item-title="name"
                    item-value="id"
                    placeholder="Select role"
                    color="primary-blue"
                    variant="solo"
                    density="compact"
                    hide-details="auto"
                    :disabled="isPending"
                    :rules="[formRules.role]"
                  ></v-select>
                </div>
              </div>
              <div class="tw-mt-3 tw-flex tw-w-full tw-items-start tw-justify-between tw-space-x-3">
                <div class="tw-flex tw-w-full">
                  <v-select
                    width="455"
                    color="primary-blue"
                    variant="solo"
                    density="compact"
                    multiple
                    return-object
                    item-title="name"
                    :items="workspaces"
                    placeholder="Select a workspace"
                    v-model="formData.workspaces"
                    hide-details="auto"
                    closable-chips
                    :disabled="isPending || formData.emails.length === 0 || !formData.role"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item @click="selectAllWorkspaces" title="Select All">
                        <template v-slot:prepend>
                          <v-checkbox-btn
                            :color="selectedSomeWorkspaces ? 'indigo-darken-4' : undefined"
                            :indeterminate="selectedSomeWorkspaces && !selectedAllWorkspaces"
                            :model-value="selectedAllWorkspaces"
                          ></v-checkbox-btn>
                        </template>
                      </v-list-item>

                      <v-divider class="mt-2"></v-divider>
                    </template>
                    <!--                    <template v-slot:item="{ props, item }">-->
                    <!--                      <v-list-item v-bind="props" :title="item.raw.name">-->
                    <!--                        <template v-slot:prepend>-->
                    <!--                          <v-avatar v-if="item.raw.image" size="16">-->
                    <!--                            <v-img sizes="16" contain :src="item.raw.image" :alt="`${item.raw.name} logo`"></v-img>-->
                    <!--                          </v-avatar>-->
                    <!--                          <v-avatar v-else :rounded="false" size="16">-->
                    <!--                            <v-img sizes="16" contain :src="partnrUpShortLogo" alt="default no-logo"></v-img>-->
                    <!--                          </v-avatar>-->
                    <!--                        </template>-->
                    <!--                      </v-list-item>-->
                    <!--                    </template>-->
                    <template v-slot:selection="{ item, index }">
                      <v-chip v-if="index < 2">
                        <v-avatar v-if="item.raw.image" size="16">
                          <v-img sizes="16" contain :src="item.raw.image" :alt="`${item.raw.name} logo`"></v-img>
                        </v-avatar>
                        <v-avatar v-else :rounded="false" size="16">
                          <v-img sizes="16" contain :src="partnrUpShortLogo" alt="default no-logo"></v-img>
                        </v-avatar>
                        <span style="font-size: 0.7rem; font-weight: 500; margin-left: 7px">{{ item.raw.name }}</span>
                      </v-chip>
                      <span v-if="index === 2" class="text-grey text-caption align-self-center">
                        (+{{ formData.workspaces.length - 2 }} workspaces)
                      </span>
                    </template>
                  </v-select>
                </div>
                <div class="tw-flex tw-w-full">
                  <v-btn
                    type="submit"
                    :loading="isPending"
                    :disabled="isPending || !isFormValid"
                    class="tw-capitalize"
                    width="141"
                    color="primary-blue"
                  >
                    Done
                  </v-btn>
                </div>
              </div>
            </div>
          </v-form>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="css"></style>
